// ────────────────────────────────────────────────────────────────────────────
//  Hopper — the bug panel.
//
//  Named for Grace Hopper, who taped a moth into a logbook and gave us the
//  word. Every uncaught error and rejected promise ends up here instead of
//  disappearing into a console nobody opened: message, stack, and a reminder
//  to run `diag` before reporting it.
//
//  Visible from the console (`hopper` shows the last catch, `hopper clear`
//  hides it) and from `__webrace.hopper()`.
// ────────────────────────────────────────────────────────────────────────────

export interface CaughtBug {
  message: string;
  stack: string;
  /** Where it came from: `error`, `promise`, or the source URL. */
  source: string;
  /** `performance.now()` when it was caught. */
  at: number;
}

/** Browser noise that is not a game bug (seen in Chrome). */
const BENIGN = [/ResizeObserver loop/i];

let last: CaughtBug | null = null;
let installed = false;

function panel(): HTMLElement | null {
  return document.getElementById("hopper");
}

/** Show the panel again (no-op if nothing was caught). */
export function showHopper(): void {
  const el = panel();
  if (el) el.classList.remove("hidden");
}

export function hideHopper(): void {
  panel()?.classList.add("hidden");
}

export function lastCaughtBug(): CaughtBug | null {
  return last;
}

function record(reason: unknown, source: string): void {
  const err = reason instanceof Error ? reason : null;
  const message = err ? `${err.name}: ${err.message}` : String(reason);
  if (!message || BENIGN.some((re) => re.test(message))) return;
  last = {
    message,
    stack: err?.stack ?? "",
    source,
    at: performance.now(),
  };
  render(last);
  // eslint-disable-next-line no-console
  console.error(`[hopper] ${message}`, err ?? "");
}

function render(bug: CaughtBug): void {
  const el = panel();
  if (!el) return;
  const msg = document.getElementById("hopper-msg");
  const stack = document.getElementById("hopper-stack");
  if (msg) msg.textContent = bug.message;
  if (stack) {
    // The first line repeats the message; keep the frames that matter.
    const lines = bug.stack.split("\n").filter((l) => !l.includes(bug.message));
    stack.textContent = lines.slice(0, 8).join("\n");
  }
  el.classList.remove("hidden");
}

/**
 * Install the global handlers. Idempotent; call once at startup, before the
 * WASM core loads so a failed init is caught too.
 */
export function installHopper(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (ev) => {
    if (!(ev instanceof ErrorEvent)) return; // resource errors are not bugs
    record(ev.error ?? ev.message, ev.filename ? `${ev.filename}:${ev.lineno}` : "error");
  });
  window.addEventListener("unhandledrejection", (ev) => {
    record(ev.reason, "promise");
  });
  document.getElementById("hopper-close")?.addEventListener("click", hideHopper);
  window.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && !panel()?.classList.contains("hidden")) hideHopper();
  });
}
